/**
 * The `?from=` and `?to=` a schedule read needs, from the two civil days the caller cares about.
 *
 * **Both carry a `Z`, and that is not decoration.** `CalendarEventController.GetCustomerSchedule`
 * runs `NormaliseRangeAtBoundary`, which answers 400 — `'from' must include a UTC marker (Z suffix)
 * or an explicit offset` — for a bare `2026-08-01`. Measured against the running backend on 13
 * August 2026, because the generated params type them as plain optional strings and say nothing.
 *
 * The window is widened by a day at each end. The backend compares against `EventDate`,
 * `StartedAt` and `FinishedAt` as instants, and no caller here converts its calendar days into
 * instants — so an occurrence on the first requested day, stamped at an hour that falls outside the
 * naive UTC bounds, would be fetched or not depending on the reader's timezone. A day of slack
 * costs one extra row of results and removes the whole class of edge.
 *
 * Promoted out of `pages/client-calendar/model/calendarRange` on 18 September 2026, on its second
 * consumer: `/zadania` reads four weeks of one client's schedule for the week strip, and a range
 * built without the marker answers 400 rather than answering wrongly.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { addDays, parseIsoDate, toIsoDate } from '~/shared/lib/calendarDate';

/** A calendar day, `YYYY-MM-DD`. */
export type IsoDay = string;

/** What the endpoint's `from` / `to` params carry. */
export interface ScheduleWindow {
  readonly from: string;
  readonly to: string;
}

/**
 * The window covering `first` through `last` inclusive, padded a day each way and UTC-marked.
 *
 * An unparseable day is passed through rather than repaired: the server's own validation is then
 * what says so, which is better than this module inventing a range nobody asked for. Two empty
 * strings come back for an empty span, which is what an unfetchable range looks like.
 */
export function scheduleWindow(first: IsoDay | undefined, last: IsoDay | undefined): ScheduleWindow {
  if (first === undefined || last === undefined) return { from: '', to: '' };
  const before = parseIsoDate(first);
  const after = parseIsoDate(last);
  return {
    from: `${before === null ? first : toIsoDate(addDays(before, -1))}T00:00:00Z`,
    to: `${after === null ? last : toIsoDate(addDays(after, 1))}T23:59:59Z`,
  };
}
